import { PropsWithChildren, useCallback, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { classNames } from "../Utils/classNames.ts";
import {
  AskConfirmation,
  ConfirmDialogCtx,
  PendingConfirmRequest,
} from "./ConfirmDialogCtx.ts";

export function ConfirmDialogProvider({ children }: PropsWithChildren) {
  const [pendingRequest, setPendingRequest] =
    useState<PendingConfirmRequest | null>(null);

  const askConfirmation = useCallback<AskConfirmation>(
    (request) =>
      new Promise<boolean>((resolve) =>
        setPendingRequest({ ...request, answer: resolve }),
      ),
    [],
  );

  function answer(confirmed: boolean) {
    pendingRequest?.answer(confirmed);
    setPendingRequest(null);
  }

  return (
    <ConfirmDialogCtx.Provider value={askConfirmation}>
      {children}
      <Dialog
        open={!!pendingRequest}
        onClose={() => answer(false)}
        className={"relative z-50"}
      >
        <DialogBackdrop className={"fixed inset-0 bg-black/30"} />
        <div className={"fixed inset-0 flex items-center justify-center p-4"}>
          <DialogPanel
            className={
              "w-full max-w-md rounded-lg bg-white p-6 shadow-xl flex flex-col gap-4"
            }
          >
            <DialogTitle className={"text-lg font-semibold text-slate-800"}>
              {pendingRequest?.title}
            </DialogTitle>
            {pendingRequest?.message && (
              <div className={"text-sm text-slate-600"}>
                {pendingRequest.message}
              </div>
            )}
            <div className={"flex justify-end gap-3 pt-2"}>
              <button
                type={"button"}
                onClick={() => answer(false)}
                className={
                  "rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                }
              >
                Cancel
              </button>
              <button
                type={"button"}
                onClick={() => answer(true)}
                className={classNames(
                  "rounded-lg px-4 py-2 text-sm font-medium text-white hover:brightness-110",
                  pendingRequest?.isDestructive ? "bg-red-600" : "bg-primary",
                )}
              >
                {pendingRequest?.confirmLabel ?? "Confirm"}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </ConfirmDialogCtx.Provider>
  );
}
